import { pageScrollBehavior } from "@/lib/scroll";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useLocale } from "@/i18n/context";
import { trackCtaClick } from "@/lib/funnel";
import { cn } from "@/lib/utils";

/**
 * Mobile-only sticky order CTA for the landing page. Slides up once the user
 * scrolls past the hero and hides for good once they reach the pricing section
 * (the conversion point), so it nudges during consideration without nagging at
 * the decision itself or overlapping the footer.
 */
export function StickyMobileCta() {
  const { t } = useLocale();
  const [heroPassed, setHeroPassed] = useState(false);
  const [reachedPricing, setReachedPricing] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const hero = document.getElementById("hero");
    const pricing = document.getElementById("pricing");
    const observers: IntersectionObserver[] = [];

    if (hero) {
      const heroObserver = new IntersectionObserver(
        ([entry]) => {
          setHeroPassed(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        },
        { threshold: 0 },
      );
      heroObserver.observe(hero);
      observers.push(heroObserver);
    }

    if (pricing) {
      const pricingObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setReachedPricing(true);
        },
        { threshold: 0 },
      );
      pricingObserver.observe(pricing);
      observers.push(pricingObserver);
    }

    return () => observers.forEach((observer) => observer.disconnect());
  }, []);

  const visible = heroPassed && !reachedPricing;

  const handleClick = () => {
    trackCtaClick("sticky_mobile_order");
    document.getElementById("pricing")?.scrollIntoView({ behavior: pageScrollBehavior() });
  };

  return (
    <div
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 border-t border-border/60 bg-background/95 px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur-md transition-transform duration-300 lg:hidden",
        visible ? "translate-y-0" : "translate-y-full",
      )}
      aria-hidden={!visible}
    >
      <Button
        onClick={handleClick}
        size="lg"
        tabIndex={visible ? 0 : -1}
        className="h-12 w-full font-semibold bg-gradient-to-r from-primary to-accent hover:from-primary-hover hover:to-primary"
      >
        {t.hero.ctaOrder}
        <ArrowRight className="ml-2 h-5 w-5" />
      </Button>
    </div>
  );
}
